import { createContext, useContext, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { offerSourcingGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerSourcingEditRight'
import { selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'

const PRAccessContext = createContext(null)

export const PRAccessProvider = ({ children, selectedSubSystemIndex = null }) => {
  const offerPermissions = useSelector(selectOfferPermissions)

  const watchedCostBudgets = useWatch({ name: 'sourcing.costBudgets' })
  const costBudgets = useMemo(() => watchedCostBudgets ?? [], [watchedCostBudgets])
  const watchedSubSystems = useWatch({ name: 'subSystems' })
  const subSystems = useMemo(() => watchedSubSystems ?? [], [watchedSubSystems])

  const value = useMemo(() => {
    const hasSelectedSubSystem = selectedSubSystemIndex !== null
    const selectedSubSystemName = hasSelectedSubSystem
      ? (subSystems?.[selectedSubSystemIndex]?.projectName ?? '')
      : null
    const hasRoleToEditPR = offerPermissions[offerSourcingGroupPolicyType.SOURCING_PR]?.edit ?? false
    const canEditPR = hasRoleToEditPR && hasSelectedSubSystem

    const getCostBudget = (costBudgetIndex) => costBudgets?.[costBudgetIndex]
    const getVendorSelections = (costBudgetIndex) => getCostBudget(costBudgetIndex)?.PR?.vendorSelections ?? []
    const getVendorDetails = (costBudgetIndex) => getCostBudget(costBudgetIndex)?.detail?.subcontractors ?? []

    const isCostBudgetActive = (costBudgetIndex) => getCostBudget(costBudgetIndex)?.detail?.isActive ?? false

    const hasVendorUploads = (costBudgetIndex) =>
      getVendorDetails(costBudgetIndex).flatMap((vendor) => vendor?.history || []).length > 0

    const canEditPRForCostBudget = (costBudgetIndex) => canEditPR && isCostBudgetActive(costBudgetIndex)

    const getAddVendorSelectionBlockReason = (costBudgetIndex) => {
      if (!hasRoleToEditPR) {
        return 'You do not have the rights to edit this PR.'
      }

      if (!hasSelectedSubSystem) {
        return 'Select a sub system before adding a vendor selection.'
      }

      if (!isCostBudgetActive(costBudgetIndex)) {
        return 'This cost budget is inactive.'
      }

      const vendorDetails = getVendorDetails(costBudgetIndex)

      const hasIncompleteVendorSelection = getVendorSelections(costBudgetIndex).some((selection) => {
        const selectedVendorId = selection?.vendor
        const selectedUploadHistoryIndex = selection?.uploadHistoryIndex

        if (!selectedVendorId) return true
        if (!Number.isInteger(selectedUploadHistoryIndex) || selectedUploadHistoryIndex < 0) return true

        const selectedVendorDetail = vendorDetails.find(
          (detail) => String(detail?.vendor ?? '') === String(selectedVendorId ?? '')
        )

        return selectedUploadHistoryIndex >= (selectedVendorDetail?.history?.length ?? 0)
      })

      if (hasIncompleteVendorSelection) {
        return 'No quotation is found in this Cost Budget'
      }

      return null
    }

    const canAddVendorSelection = (costBudgetIndex) => getAddVendorSelectionBlockReason(costBudgetIndex) === null

    const canDeleteVendorSelection = (costBudgetIndex, vendorSelectionIndex) => {
      if (!canEditPRForCostBudget(costBudgetIndex)) {
        return false
      }

      const currentVendorSelection = getVendorSelections(costBudgetIndex)?.[vendorSelectionIndex]
      return (currentVendorSelection?.submissions?.length ?? 0) === 0
    }

    const canSelectVendor = (costBudgetIndex, vendorSelectionIndex) => {
      if (!canEditPRForCostBudget(costBudgetIndex) || !hasVendorUploads(costBudgetIndex)) {
        return false
      }

      const currentVendorSelection = getVendorSelections(costBudgetIndex)?.[vendorSelectionIndex]
      return (currentVendorSelection?.submissions?.length ?? 0) === 0
    }

    return {
      canEditPR,
      hasSelectedSubSystem,
      selectedSubSystemName,
      hasRoleToEditPR,
      canEditPRForCostBudget,
      canEditPRSubmission: canEditPRForCostBudget,
      canAddVendorSelection,
      getAddVendorSelectionBlockReason,
      canDeleteVendorSelection,
      canSelectVendor,
      hasVendorUploads,
      isCostBudgetActive,
    }
  }, [costBudgets, offerPermissions, selectedSubSystemIndex, subSystems])

  return <PRAccessContext.Provider value={value}>{children}</PRAccessContext.Provider>
}

export const usePRAccess = () => {
  const context = useContext(PRAccessContext)

  if (!context) {
    throw new Error('usePRAccess must be used within PRAccessProvider')
  }

  return context
}
